import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { Table, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { frontendDisplayedCourseSelector } from '@/atoms/auth'
import { viewingStateAtom, ViewingStateType } from '@/atoms/viewingState'
import { getPracticeProblemNoteSummaries } from '@/api/cb/contentCreatorContent'
import { PracticeProblemNoteSummary } from 'common/src/api/contentFrontendVsContentBackend/contentCreatorNotes'
import { PracticeProblemType } from 'common/src/practiceProblems/consts'
import styles from './ContentCreatorNotes.module.less'

interface PracticeProblemNoteSummaryWithOrder
    extends PracticeProblemNoteSummary {
    outlineOrder: number
}

export const PracticeProblemNotesTable: React.FC<
    unknown
> = (): ReactElement => {
    const courseName = useRecoilValue(frontendDisplayedCourseSelector)
    const setViewingState = useSetRecoilState(viewingStateAtom)
    const [rawSummaries, setRawSummaries] = useState<
        PracticeProblemNoteSummary[]
    >([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (!courseName) return
        setLoading(true)
        getPracticeProblemNoteSummaries(courseName).then((response) => {
            if (!response.data.isError) {
                setRawSummaries(response.data.payload.summaries)
            }
            setLoading(false)
        })
    }, [courseName])

    const summaries = useMemo(
        (): PracticeProblemNoteSummaryWithOrder[] =>
            rawSummaries.map((s, i) => ({ ...s, outlineOrder: i })),
        [rawSummaries]
    )

    const navigateToProblem = useCallback(
        (summary: PracticeProblemNoteSummary): void => {
            setViewingState({
                contentPath: summary.contentPath,
                viewingStateType: ViewingStateType.practiceProblems,
                id: summary.id,
            })
        },
        [setViewingState]
    )

    const allTopics = [
        ...new Set(summaries.map((s) => s.topic).filter(Boolean) as string[]),
    ].sort()
    const allSeasons = [
        ...new Set(
            summaries
                .map((s) => s.previousExamLabel?.split(' ')[0])
                .filter(Boolean) as string[]
        ),
    ].sort()
    const allYears = [
        ...new Set(
            summaries
                .map((s) => s.previousExamLabel?.split(' ')[1])
                .filter(Boolean) as string[]
        ),
    ].sort()
    const allExamNames = [
        ...new Set(
            summaries
                .map((s) => s.previousExamLabel?.split(' ')[2])
                .filter(Boolean) as string[]
        ),
    ].sort()
    const allQuestionNumbers = [
        ...new Set(
            summaries
                .map((s) => s.previousExamLabel?.split(' ')[3])
                .filter(Boolean) as string[]
        ),
    ].sort()

    const columns: ColumnsType<PracticeProblemNoteSummaryWithOrder> = [
        {
            title: 'Section',
            dataIndex: 'sectionName',
            key: 'sectionName',
            width: 180,
            sorter: (a, b) => a.outlineOrder - b.outlineOrder,
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 100,
            filters: Object.values(PracticeProblemType).map((t) => ({
                text: t,
                value: t,
            })),
            onFilter: (value, record) => record.type === value,
        },
        {
            title: 'Season',
            key: 'examSeason',
            width: 90,
            filters: allSeasons.map((s) => ({ text: s, value: s })),
            onFilter: (value, record) =>
                record.previousExamLabel?.split(' ')[0] === value,
            render: (_: unknown, record) =>
                record.previousExamLabel?.split(' ')[0] ?? '-',
        },
        {
            title: 'Year',
            key: 'examYear',
            width: 80,
            filters: allYears.map((y) => ({ text: y, value: y })),
            onFilter: (value, record) =>
                record.previousExamLabel?.split(' ')[1] === value,
            render: (_: unknown, record) =>
                record.previousExamLabel?.split(' ')[1] ?? '-',
        },
        {
            title: 'Exam',
            key: 'examName',
            width: 100,
            filters: allExamNames.map((e) => ({ text: e, value: e })),
            onFilter: (value, record) =>
                record.previousExamLabel?.split(' ')[2] === value,
            render: (_: unknown, record) =>
                record.previousExamLabel?.split(' ')[2] ?? '-',
        },
        {
            title: 'Question',
            key: 'examQuestion',
            width: 90,
            filters: allQuestionNumbers.map((q) => ({ text: q, value: q })),
            onFilter: (value, record) =>
                record.previousExamLabel?.split(' ')[3] === value,
            render: (_: unknown, record) =>
                record.previousExamLabel?.split(' ')[3] ?? '-',
        },
        {
            title: 'Points',
            dataIndex: 'points',
            key: 'points',
            width: 70,
            sorter: (a, b) => a.points - b.points,
        },
        {
            title: 'Topic',
            dataIndex: 'topic',
            key: 'topic',
            width: 120,
            filters: allTopics.map((t) => ({ text: t, value: t })),
            onFilter: (value, record) => record.topic === value,
            render: (val: string | undefined) => val ?? '-',
        },
        {
            title: 'Gold?',
            dataIndex: 'isGoldStandard',
            key: 'isGoldStandard',
            width: 70,
            render: (val: boolean | undefined) => (val ? 'Yes' : '-'),
            filters: [
                { text: 'Yes', value: true },
                { text: 'No', value: false },
            ],
            onFilter: (value, record) =>
                value === true
                    ? record.isGoldStandard === true
                    : !record.isGoldStandard,
        },
        {
            title: 'My Notes',
            dataIndex: 'myNotes',
            key: 'myNotes',
            width: 250,
            ellipsis: true,
            render: (val: string) =>
                val ? (
                    <Tooltip
                        title={
                            <span style={{ whiteSpace: 'pre-wrap' }}>
                                {val}
                            </span>
                        }
                        placement="topLeft"
                        overlayStyle={{ maxWidth: 500 }}
                    >
                        <span className={styles.notesPreview}>{val}</span>
                    </Tooltip>
                ) : (
                    '-'
                ),
        },
        {
            title: 'Link',
            key: 'link',
            width: 60,
            render: (_: unknown, record: PracticeProblemNoteSummary) => (
                <a
                    className={styles.linkButton}
                    onClick={() => navigateToProblem(record)}
                >
                    View
                </a>
            ),
        },
    ]

    return (
        <div className={styles.contentCreatorNotesTable}>
            <div className={styles.title}>Practice Problem Notes</div>
            <Table
                columns={columns}
                dataSource={summaries}
                rowKey="id"
                loading={loading}
                size="small"
                pagination={{ pageSize: 50, showSizeChanger: true }}
                scroll={{ x: 1400 }}
            />
        </div>
    )
}
